/**
 * TimelineHeader Component
 *
 * X-axis header with month labels, sun intensity, day numbers, frost markers, and NOW line.
 * Synchronized horizontal scroll with content area.
 */

import React, { useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import { useTimelineStaticContext } from '../context/timeline-static-context'
import { generateWeeksAndMonths, getNowDateString } from '../utils/timeline-date-utils'
import { X_AXIS_HEIGHT } from '../timeline-constants'
import { TimelineMonthLabels } from './header/timeline-month-labels'
import { TimelineSunIntensity } from './header/timeline-sun-intensity'
import { TimelineDayNumbers } from './header/timeline-day-numbers'
import { TimelineHeaderReportMarkers } from './header/timeline-header-report-markers'
import { TimelineHeaderNowLine } from './header/timeline-header-now-line'
import { useTimelineDragPan } from '../hooks/use-timeline-drag-pan'

/**
 * TimelineHeader - X-axis header container
 *
 * Features:
 * - Month labels with year (spans multiple weeks)
 * - Sun intensity gradient row (cosine wave)
 * - Day numbers with alternating background
 * - Frost date markers (LFD/FFD badges + dashed lines)
 * - NOW line indicator (badge + vertical line)
 * - Synchronized horizontal scroll with content
 */
export const TimelineHeader = () => {
  const {
    TIMELINE_WIDTH,
    NOW_X,
    weekWidth,
    headerRef,
    contentRef,
    timelineDateRange,
    totalWeeks,
    hideNowLine,
  } = useTimelineStaticContext()

  const { isDragging, handleMouseDown, cursor } = useTimelineDragPan({ contentRef })

  const theme = useTheme()

  // Generate weeks and months data
  const { weeks, months } = useMemo(
    () => generateWeeksAndMonths(timelineDateRange, totalWeeks),
    [timelineDateRange, totalWeeks]
  )

  // Current date string
  const NOW_DATESTRING = getNowDateString()

  return (
    <Box
      ref={headerRef}
      onMouseDown={handleMouseDown}
      sx={{
        gridColumn: 1,
        gridRow: 1,
        overflow: 'hidden',
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: 20,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : cursor,
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      {/* Main SVG — NOW line is inside so it inherits the zoom scaleX transform */}
      <svg width={TIMELINE_WIDTH} height={X_AXIS_HEIGHT} style={{ display: 'block' }}>
        {/* Month headers */}
        <TimelineMonthLabels months={months} weekWidth={weekWidth} />

        {/* Sun intensity row */}
        <TimelineSunIntensity weeks={weeks} weekWidth={weekWidth} />

        {/* Day numbers */}
        <TimelineDayNumbers weeks={weeks} weekWidth={weekWidth} />

        {/* Report markers */}
        <TimelineHeaderReportMarkers />

        {/* NOW line — last so it renders on top */}
        {!hideNowLine && <TimelineHeaderNowLine x={NOW_X} dateString={NOW_DATESTRING} />}
      </svg>
    </Box>
  )
}
